import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import './SearchBar.css';

const SearchBar = () => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef(null);

  // Rastgele parçacıklar için
  const [particles, setParticles] = useState([]);

  // Yazma efekti için
  useEffect(() => {
    if (search.length > 0) {
      setIsTyping(true);
      
      // Yazma durduktan 1 saniye sonra efekti kapat
      if (typingTimeout) clearTimeout(typingTimeout);
      const timeout = setTimeout(() => {
        setIsTyping(false);
      }, 1000);
      
      setTypingTimeout(timeout);
    } else {
      setIsTyping(false);
    }
    
    return () => {
      if (typingTimeout) clearTimeout(typingTimeout);
    };
  }, [search]);

  // Fokus olduğunda parçacıklar oluştur
  useEffect(() => {
    if (isFocused) {
      createInitialParticles();
    } else {
      setParticles([]);
    }
  }, [isFocused]);

  // Rastgele parçacıklar oluştur
  const createInitialParticles = () => {
    const newParticles = [];
    for (let i = 0; i < 15; i++) {
      newParticles.push({
        id: Math.random(),
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 1,
        color: getRandomColor(),
        duration: Math.random() * 3 + 2
      });
    }
    setParticles(newParticles);
  };

  // Rastgele renk üret
  const getRandomColor = () => {
    const colors = ['#00FFFF', '#33ccff', '#00ff99', '#66ffcc', '#ffff00'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    
    if (!search.trim()) return;
    
    // Chatbot'u açmak için
    const chatButton = document.querySelector('.chat-button');
    if (chatButton) {
      chatButton.click();
    }
    
    // Parçacık patlaması efekti
    createSubmitParticles();
    
    // Chatbot'a mesaj göndermek için
    setTimeout(() => {
      const chatInput = document.querySelector('.chat-input textarea');
      const sendButton = document.querySelector('.chat-input button');
      
      if (chatInput && sendButton) {
        // Input'a sorguyu yerleştir
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLTextAreaElement.prototype, "value"
        ).set;
        nativeInputValueSetter.call(chatInput, search);
        
        // Input event'ini tetikle
        const inputEvent = new Event('input', { bubbles: true });
        chatInput.dispatchEvent(inputEvent);
        
        // Gönder butonuna tıkla
        sendButton.click();
      }
    }, 500);
    
    // Arama kutusunu temizle
    setSearch('');
    setIsTyping(false);
  };

  // Gönderimde özel parçacık efekti
  const createSubmitParticles = () => {
    const newParticles = [];
    for (let i = 0; i < 30; i++) {
      newParticles.push({
        id: Math.random(),
        x: 50 + (Math.random() * 40 - 20),
        y: 50 + (Math.random() * 40 - 20), 
        size: Math.random() * 6 + 2,
        color: getRandomColor(),
        duration: Math.random() * 1.5 + 0.5
      });
    }
    setParticles(newParticles);
    
    // Parçacıkları bir süre sonra temizle
    setTimeout(() => {
      setParticles([]);
    }, 2000);
  };

  return (
    <div className="search-bar-container">
      <motion.div 
        className={`search-bar-wrapper ${isFocused ? 'focused' : ''} ${isHovered ? 'hovered' : ''}`}
        whileHover={{ scale: 1.02 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          type: "spring", 
          stiffness: 500, 
          damping: 30 
        }}
      >
        {/* Parçacık Efektleri */}
        <div className="particle-container">
          <AnimatePresence>
            {particles.map(particle => (
              <motion.div
                key={particle.id}
                className="particle"
                initial={{ 
                  x: `50%`, 
                  y: `50%`, 
                  opacity: 1,
                  scale: 0
                }}
                animate={{ 
                  x: `${particle.x}%`, 
                  y: `${particle.y}%`, 
                  opacity: 0,
                  scale: particle.size
                }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ 
                  duration: particle.duration,
                  ease: "easeOut"
                }}
                style={{
                  backgroundColor: particle.color
                }}
              />
            ))}
          </AnimatePresence>
        </div>

        {/* Neon kenar efekti */}
        <div className={`neon-border ${isFocused ? 'active' : ''}`}>
          <div className="neon-glow"></div>
        </div>

        <form onSubmit={handleSearchSubmit}>
          {/* Arama ikonu */}
          <motion.div 
            className="search-icon"
            animate={{ 
              scale: isTyping ? 1.2 : 1,
              rotate: isTyping ? [0, -10, 0, 10, 0] : 0
            }}
            transition={{ 
              scale: { duration: 0.3 },
              rotate: { duration: 0.5, repeat: isTyping ? Infinity : 0, repeatDelay: 0.5 }
            }}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </motion.div>

          <input
            type="text"
            placeholder={t('searchBar.placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className="hero-search-input"
            ref={inputRef}
            aria-label={t('searchBar.ariaLabel')}
          />

          {/* Pulse animasyonu - Enter basılacağını belirtmek için */}
          <motion.div 
            className="enter-hint"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: isFocused && search.length > 0 ? [0.3, 0.7, 0.3] : 0
            }}
            transition={{ 
              duration: 1.5,
              repeat: Infinity
            }}
          >
            <motion.div 
              className="enter-key"
              whileHover={{ scale: 1.1 }}
            >
              <span>Enter</span>
            </motion.div>
          </motion.div>
          
          {/* Yazı bulutu - yazarken görünür */}
          <AnimatePresence>
            {isTyping && (
              <motion.div 
                className="typing-particles"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="typing-particle"
                    initial={{ 
                      x: 0, 
                      y: 0, 
                      opacity: 0.7,
                      scale: 0.5 
                    }}
                    animate={{ 
                      x: Math.random() * 40 - 20, 
                      y: -20 - Math.random() * 30,
                      opacity: 0,
                      scale: Math.random() * 0.5 + 0.5
                    }}
                    transition={{ 
                      duration: Math.random() * 1 + 0.5,
                      repeat: Infinity,
                      repeatDelay: Math.random() * 0.5
                    }}
                    style={{
                      left: `${30 + i * 10}%`
                    }}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </motion.div>
    </div>
  );
};

export default SearchBar;